import { Card } from '@uhg-abyss/web/ui/Card';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Box } from '@uhg-abyss/web/ui/Box';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import AddCustomer from './AddProfile/AddCustomer';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Tabs } from '@uhg-abyss/web/ui/Tabs';
import AddFileDetails from './AddProfile/AddFileDetails';
import AddPopulationExclusion from './AddProfile/AddPopulationExclusion';
import AddDemographics from './AddProfile/AddDemographics';
import AddThresholdProcessing from './AddProfile/AddThresholdProcessing';
const AddRules = React.lazy(() => import('./AddProfile/AddRules'));

import { setLovData } from '@src/store/slices/ListofValuesSlice';

import {
  useLazyAddProfileQuery,
  useLazyGetProfileByVersionQuery,
  useLazyGetProfileListQuery,
} from '@src/store/services/profileServices';
import { useDispatch, useSelector } from 'react-redux';
import {
  convertStrintoArray,
  convertToCST,
  modifyArray,
  populationValidation,
  validateInput,
  validateInputs,
  validateNextExpedAndEarlyStart,
  validatePolicyNumber,
  validateProfile,
  validateStreetorCity,
  validateVersionEndDate,
  encryptProfileParams,
} from '@src/utils';
import { DestinationCheck, LABELS } from '@src/constants';
import { setdysData } from '@src/store/slices/destinationSystemSlice';
import { useAuth } from '@src/Providers/AuthProvider';
import { Errors, List, CopyData } from './Profile.types';
import React from 'react';

type ErrorMessagess = string;
type ErrorMessages = ErrorMessagess[];

const AddProfile = () => {
  const dispatch = useDispatch();
  const [profileAllFields] = useLazyGetProfileListQuery();
  const [addProfile] = useLazyAddProfileQuery();
  const [list, setList] = useState<List>();
  const [profileByversion] = useLazyGetProfileByVersionQuery();
  const [copyData, setCopyData] = useState<CopyData>({});

  const submitterDetails = useSelector(
    (state: { listOfValues: { submitterData: any } }) =>
      state?.listOfValues?.submitterData
  );

  useEffect(() => {
    const handleFetchFieldsData = async () => {
      const { data } = await profileAllFields({});
      dispatch(setLovData(data));
      setList(data);
    };
    handleFetchFieldsData();
  }, []);
  const onVersionChange = async () => {
    const { data } = await profileByversion({
      submitterAssignId: submitterDetails?.submitterAssignId,
      submitterGroupId: submitterDetails?.submitterGroupId,
      submitterGroupName: submitterDetails?.submitterGroupName,
      versionNumber: +submitterDetails?.versionNumber,
      destinationSystem: submitterDetails?.destinationSystem,
    });
    setCopyData(data);
  };

  const router = useRouter();
  const isCopy = router?.getLocation()?.pathname?.includes('/copy');
  const listAndDestination = useSelector((state) => state);
  const { destinationSystem, listOfValues }: any = listAndDestination;
  const systemType = destinationSystem?.data;
  const isGPSorPRIME =
    systemType === DestinationCheck.GPS ||
    systemType === DestinationCheck.PRIME;
  const isPrime = systemType === DestinationCheck.PRIME;
  const isGps = systemType === DestinationCheck.GPS;
  const isCDb = systemType === DestinationCheck?.CDB;

  const ruleData = isGps
    ? list?.gpsDefltRulesLst
    : isPrime
    ? list?.primeDefltRulesLst
    : list?.cdbDefltRulesLst;

  let data = {
    customerDetails: {
      complexityRating: '0',

      securityAccess: '0',
      marketSegment: '2',
      employerGroups: [
        {
          employerGroupId: '',
          displayBlockNumber: '0',
        },
        {
          employerGroupId: '',
          displayBlockNumber: '1',
        },
        {
          employerGroupId: '',
          displayBlockNumber: '2',
        },
        {
          employerGroupId: '',
          displayBlockNumber: '3',
        },
      ],
    },
    fileDetails: {
      vbdMappingIndicator: '2',
      fileIndicator: '1',
      feedVendor: '0',
    },
    thresholdProcessing: {
      autoCancelIndicator: '2',
      ...(systemType === '5' && { submitterGroupPopulation: 0 }),
    },
    demographics: {
      openEnrollmentDate: '01/01',
      preExistingInfoIndicator: 'N',
      enrollmentReasonCodeRequired: 'N',
    },
    populationExclusions: {
      Type: [],
    },

    rulesMaintenance: ruleData,
  };

  const form = useForm({
    defaultValues: isCopy
      ? {
          ...copyData,
          customerDetails: {
            ...copyData?.customerDetails,
            submitterAssignId: '',
            submitterGroupName: '',
            versionEffectiveDate: '',
            versionEndDate: '',
          },
          fileDetails: { ...copyData?.fileDetails, nextFileExpectedDate: '' },
        }
      : {
          ...data,
        },
  });

  useEffect(() => {
    if (isCopy && copyData && Object.keys(copyData).length)
      form.reset({
        ...copyData,
        customerDetails: {
          ...copyData?.customerDetails,
          submitterAssignId: '',
          submitterGroupName: '',
          versionEffectiveDate: '',
          versionEndDate: '',
        },
        fileDetails: { ...copyData?.fileDetails, nextFileExpectedDate: '' },
      });
  }, [copyData]);

  useEffect(() => {
    if (!isCopy) {
      const ruleData = isGps
        ? list?.gpsDefltRulesLst
        : isPrime
        ? list?.primeDefltRulesLst
        : list?.cdbDefltRulesLst;
      form.reset({
        ...form.watch(),
        rulesMaintenance: ruleData,
      });
    }
  }, [systemType]);

  useEffect(() => {
    if (isCopy) {
      onVersionChange();
    }
  }, [isCopy]);

  const { user } = useAuth();

  const [errorMessages, setErrorMessages] = useState<ErrorMessages>([]);

  const destinationSyst = form.watch('customerDetails.destinationSystem');

  const isCdbORGps = isGps || isCDb;

  useEffect(() => {
    dispatch(setdysData(destinationSyst));
  }, [destinationSyst]);

  const handleSubmit = () => {
    let errors: Errors = {
      customerIdError: validateInputs(
        LABELS.ASSIGN_SUBMISSION_ID,
        form.watch('customerDetails.submitterAssignId')
      ),
      customerSubmissionName: validateInputs(
        LABELS.CUSTOMER_SUBMISSION_NAME,
        form.watch('customerDetails.submitterGroupName')
      ),
      ...((isCDb || destinationSyst === '8') && {
        customerNumber: validateInputs(
          LABELS.CUSTOMER_NUMBER,
          form.watch('customerDetails.customerNumber')
        ),
        polocyNumberError: validatePolicyNumber(
          LABELS.POLOCY_NUMBER,
          form.watch('customerDetails.policyNumber')
        ),
      }),
      profileStatus: validateProfile(
        LABELS.PROFILE_STATUS,
        form.watch('customerDetails.profileStatus')
      ),
      profileType: validateProfile(
        LABELS.PROFILE_TYPE,
        form.watch('customerDetails.profileType')
      ),
      destinationSystemError: validateStreetorCity(
        'Destination System',
        form.watch('customerDetails.destinationSystem')
      ),

      ...(form.watch('customerDetails.versionEndDate') && {
        VersionEndError: validateVersionEndDate(
          'Version End Date',
          form.watch('customerDetails.versionEffectiveDate'),
          form.watch('customerDetails.versionEndDate')
        ),
      }),
      fileFrequencyError: validateInputs(
        LABELS.FILE_FREQUENCY,
        form.watch('fileDetails.frequencyCode')
      ),
      fileFormatError: validateInputs(
        LABELS.FILE_FORMAT_CODE,
        form.watch('fileDetails.fileFormatTypeCode')
      ),
      eligilbitysoucseError: validateInputs(
        LABELS.ELIGYBILTY_CODE,
        form.watch('fileDetails.eligibilitySourceType')
      ),
      versionEffectiveDate: validateInput(
        LABELS.VERSION_EFFECTIVE_DATE,
        form.watch('customerDetails.versionEffectiveDate')
      ),
      versionEndDateError: validateNextExpedAndEarlyStart(
        LABELS.VERSION_END_DATE,
        form.watch('customerDetails.versionEndDate')
      ),
      channelTypeError: validateInputs(
        LABELS.CHANNEL_TYPE,
        form.watch('fileDetails.transmitCode')
      ),
      earlyStartDateError: validateNextExpedAndEarlyStart(
        'Earliest Start Date',
        form.watch('demographics.earliestStartDate')
      ),
      nextFilexpectDateError: validateNextExpedAndEarlyStart(
        'Next File Expected Date',
        form.watch('fileDetails.nextFileExpectedDate')
      ),

      ...(isCdbORGps && {
        profilePopulationsError: populationValidation(
          form.watch('thresholdProcessing.submitterGroupPopulation')
        ),
      }),
    };

    const errorMessag: string[] = Object.values(errors).filter(
      (error): error is string => error !== null && error !== undefined
    );

    setErrorMessages(errorMessag);
    if (errorMessag?.length === 0) {
      const data = form.getValues();

      const ids =
        form
          ?.getValues('rulesMaintenance')
          ?.map((nameObj) => {
            const detail = list?.rulesList?.find(
              (detail) => detail?.name === nameObj?.name
            );
            return detail ? detail.id : null;
          })
          ?.filter((id) => id !== null) ?? [];
      const modifiedArray = modifyArray(data?.thresholdProcessing?.percentages);

      const gpsForm = form.watch('customerDetails.employerGroups');

      const thresholdPrecentage =
        systemType === '8' ||
        systemType === 'G' ||
        destinationSyst === '8' ||
        destinationSyst === 'G'
          ? data?.thresholdProcessing?.percentages?.filter(
              (item) => item !== null
            )
          : modifiedArray;

      const formatted = gpsForm?.map((item) => ({
        employerGroupId: item?.employerGroupId,
        displayBlockNumber: item?.displayBlockNumber.toString(),
      }));
      const typeCodeField =
        data?.populationExclusions?.TypeCodeField === ''
          ? []
          : data?.populationExclusions?.TypeCodeField;

      const CancelReasonFied =
        data?.populationExclusions?.CancelReasonField === ''
          ? []
          : data?.populationExclusions?.CancelReasonField;
      const populationEligibilityStatusField =
        data?.populationExclusions?.EligibilityStatusField === ''
          ? []
          : data?.populationExclusions?.EligibilityStatusField;

      const typePopulation =
        Array.isArray(data?.populationExclusions?.Type) &&
        data?.populationExclusions?.Type?.some(
          (type) => type !== '' && type !== null && type !== undefined
        )
          ? data?.populationExclusions?.Type.filter(
              (type) => type !== '' && type !== null && type !== undefined
            )
          : [];

      const payload = {
        customerDetails: {
          submitterAssignId: data?.customerDetails?.submitterAssignId,

          submitterGroupName: data?.customerDetails?.submitterGroupName,
          ...((isCDb || destinationSyst === '8') && {
            customerNumber: data?.customerDetails?.customerNumber,
            policyNumber: data?.customerDetails?.policyNumber,
            billingEntity: data?.customerDetails?.billingEntity,
          }),

          destinationSystem: data?.customerDetails?.destinationSystem,
          legalEntity: data?.customerDetails?.legalEntity,
          profileType: data?.customerDetails?.profileType,
          profileStatus: data?.customerDetails?.profileStatus,

          marketSegment: data?.customerDetails.marketSegment,
          securityAccess: data?.customerDetails.securityAccess,
          complexityRating: +data?.customerDetails?.complexityRating,
          versionEffectiveDate: convertToCST(
            data?.customerDetails?.versionEffectiveDate
          ),
          versionEndDate: convertToCST(data?.customerDetails?.versionEndDate),
          ...(isGps && {
            employerGroups: formatted.filter((entry) => entry?.employerGroupId),
          }),
        },
        fileDetails: {
          ...data?.fileDetails,
          nextFileExpectedDate: convertToCST(
            data?.fileDetails?.nextFileExpectedDate
          ),
        },

        ...((systemType === '8' || destinationSyst === '8') && {
          demographics: {
            earliestStartDate: convertToCST(
              data?.demographics?.earliestStartDate
            ),
            openEnrollmentDate: data.demographics?.openEnrollmentDate || '',
            emailNotificationCode:
              data?.demographics?.emailNotificationCode || '',
            enrollmentReasonCodeRequired:
              data.demographics?.enrollmentReasonCodeRequired || '',
            preExistingInfoIndicator:
              data.demographics?.preExistingInfoIndicator || '',
            domesticStreetAddress1:
              data.demographics?.domesticStreetAddress1 || '',
            domesticStreetAddress2:
              data.demographics?.domesticStreetAddress2 || '',
            domesticCity: data.demographics?.domesticCity || '',
            domesticStateCode: data.demographics?.domesticStateCode || '',
            domesticPostalCode: data.demographics?.domesticPostalCode || '',
            domesticPostalExtensionCode:
              data.demographics?.domesticPostalExtensionCode || '',
          },
        }),
        thresholdProcessing: {
          submitterGroupPopulation:
            +data.thresholdProcessing?.submitterGroupPopulation,
          autoCancelIndicator: +data?.thresholdProcessing?.autoCancelIndicator,
          percentages: thresholdPrecentage,
        },
        rulesMaintenance: ids,
        ...((systemType === '8' || destinationSyst === '8') && {
          populationExclusions: {
            Type: typePopulation,
            TypeCodeField: typeCodeField,

            CancelReasonField: CancelReasonFied,
            EligibilityStatusField: populationEligibilityStatusField,
            PlanVariationCodeField: convertStrintoArray(
              data?.populationExclusions?.PlanVariationCodeField
            ),
            ReportingCodeField: convertStrintoArray(
              data?.populationExclusions?.ReportingCodeField
            ),
            MemberUtilityTextField: convertStrintoArray(
              data?.populationExclusions?.MemberUtilityTextField
            ),
            CoverageUtilityField: convertStrintoArray(
              data?.populationExclusions?.CoverageUtilityField
            ),
            EligibilityUtilityTextField: convertStrintoArray(
              data?.populationExclusions?.EligibilityUtilityTextField
            ),
          },
        }),
        profileDetails: {
          eeUserId: user?.userId,
        },
      };

      const addDataSubmission = async () => {
        const addResponse = await addProfile(payload);

        if (addResponse?.isSuccess) {
          const { versionNumber, submitterAssignId, destinationSystem } =
            addResponse?.data?.customerDetails;
          router.navigate(
            `/profile/${encryptProfileParams(
              versionNumber,
              submitterAssignId,
              destinationSystem
            )}`
          );
        } else {
          setErrorMessages(
            addResponse?.error?.response?.data?.validationErrors
          );
          window?.scrollTo({ top: 0, behavior: 'smooth' });
        }
      };
      addDataSubmission();
    } else {
      window?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      {(errorMessages?.length >= 1 || isCopy) && (
        <Card>
          {errorMessages?.length >= 1 && (
            <Box color="$web.semantic.color.surface.container.secondary">
              <div>
                <Text asElement="label" css={{ color: 'red' }}>
                  Validation Errors:
                </Text>
                <ul>
                  {errorMessages.map((item, index) => (
                    <Text
                      css={{
                        color: ' #FF0000',
                        listStyle: 'initial',
                        fontWeight: 'bold',
                      }}
                    >
                      <li key={index}>{item}</li>
                    </Text>
                  ))}
                </ul>
              </div>
            </Box>
          )}
          {isCopy && (
            <Box
              color="$web.semantic.color.surface.container.secondary"
              align="end"
            >
              Version: 1
            </Box>
          )}
        </Card>
      )}

      <FormProvider state={form}>
        <Card
          css={{
            border: 0,
            minHeight: '80vh',
            '.abyss-table-cell': {
              paddingTop: '5px',
              paddingBottom: '5px',
              // width:"60%"
            },
            '.abyss-select-input-container,.abyss-text-input': {
              minHeight: '20px',
              maxWidth: '100%',
              lineHeight: '0.9',
              borderRadius: '0',
            },
            '.abyss-select-input-root .abyss-label-container,.abyss-text-input-root .abyss-label-container':
              {
                display: 'none',
              },
            '.abyss-select-input-container-wrapper,.abyss-text-input-container-wrapper':
              {
                minHeight: '20px',
                maxWidth: '100%',
                lineHeight: '0.9',
                height: '26px',
                borderRadius: '0',
              },
          }}
        >
          <div style={{ padding: '16px' }}>
            <Grid>
              <Suspense fallback="loading..">
                <AddCustomer />
              </Suspense>
              <Grid.Col
                css={{
                  width: '100%',
                }}
              >
                <Tabs
                  title="Display - Row"
                  css={{
                    '[role="tablist"]': {
                      width: '100%',
                    },
                    '.abyss-tabs-tab': {
                      flex: 1,
                    },
                    '.abyss-tabs-tab-content': {
                      fontSize: '$web.core.font-size.p.60',
                      color: '$web.semantic.color.text.content.primary',
                    },
                    '.abyss-table-cell:first-child': {
                      color: '$web.semantic.color.text.content.primary',
                      fontWeight: 'bold',
                    },
                  }}
                >
                  <Tabs.Tab label="File Details">
                    <Suspense fallback="loading..">
                      <AddFileDetails />
                    </Suspense>
                  </Tabs.Tab>

                  {isGPSorPRIME ||
                  destinationSyst === '5' ||
                  destinationSyst === 'G' ? (
                    ' '
                  ) : (
                    <Tabs.Tab label="Demographics">
                      <Suspense fallback="loading..">
                        <AddDemographics />
                      </Suspense>
                    </Tabs.Tab>
                  )}

                  <Tabs.Tab label="Threshold Processing">
                    <Suspense fallback="loading..">
                      <AddThresholdProcessing />
                    </Suspense>
                  </Tabs.Tab>
                  <Tabs.Tab label="Rules Maintenance">
                    <Suspense fallback="loading..">
                      <AddRules />
                    </Suspense>
                  </Tabs.Tab>
                  {isGPSorPRIME ||
                  destinationSyst === '5' ||
                  destinationSyst === 'G' ? (
                    ' '
                  ) : (
                    <Tabs.Tab label="Population Exclusions">
                      <Suspense fallback="loading..">
                        <AddPopulationExclusion />
                      </Suspense>
                    </Tabs.Tab>
                  )}
                </Tabs>
              </Grid.Col>
              <Grid.Col
                css={{
                  display: 'flex',
                  width: '100%',
                  gap: '1rem',
                  justifyContent: 'flex-end',
                  '.abyss-button-content-container': {
                    fontSize: '$web.core.font-size.p.60',
                  },
                }}
              >
                {
                  <>
                    <Button
                      onClick={handleSubmit}
                      size="sm"
                      variant="outline"
                      className="no-margin-left no-padding-left no-padding-right"
                    >
                      <strong>SAVE</strong>
                    </Button>
                  </>
                }
              </Grid.Col>
            </Grid>
          </div>
        </Card>
      </FormProvider>
    </>
  );
};

export default AddProfile;
